import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Upload, CheckCircle, Clock, AlertCircle, Copy, Check } from 'lucide-react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSalePage } from '../../contexts/SalePageContext';
import { collection, addDoc, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { PaymentCheckoutData, PaymentRecord } from '../../types/salepage';
import SalePageNavbar from '../../components/SalePageNavbar';

const SalePagePayment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const { subscription, refreshSubscription } = useSalePage();
  
  const checkoutData = location.state as PaymentCheckoutData | null;
  
  const [paymentRef, setPaymentRef] = useState<string>('');
  const [slipFile, setSlipFile] = useState<File | null>(null);
  const [slipPreview, setSlipPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'uploaded' | 'verifying'>('pending');
  const [copied, setCopied] = useState(false);
  const [expiryTime, setExpiryTime] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  const PROMPTPAY_NUMBER = '0812345678';
  const PROMPTPAY_NAME = 'EduMi Co., Ltd.';

  useEffect(() => {
    if (!checkoutData || !user) {
      navigate('/salepage/checkout');
      return;
    }

    const expiry = new Date(Date.now() + 30 * 60 * 1000);
    setExpiryTime(expiry);
    
    const ref = `SP${Date.now()}${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
    setPaymentRef(ref);
  }, [checkoutData, user, navigate]);

  useEffect(() => {
    if (!expiryTime) return;

    const interval = setInterval(() => {
      const now = new Date();
      const diff = expiryTime.getTime() - now.getTime();
      
      if (diff <= 0) {
        setTimeRemaining('หมดเวลา');
        clearInterval(interval);
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    }, 1000);

    return () => clearInterval(interval);
  }, [expiryTime]);

  const handleSlipUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('ไฟล์ใหญ่เกิน 5MB');
      return;
    }

    setSlipFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setSlipPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitPayment = async () => {
    if (!slipFile || !user || !checkoutData) {
      alert('กรุณาอัปโหลดสลิปการโอนเงิน');
      return;
    }

    setIsUploading(true);
    setPaymentStatus('verifying');

    try {
      // Check if user has verified email (from Course system)
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      const isEmailVerified = userData?.emailVerified === true;

      const slipRef = ref(storage, `payment-slips/${user.uid}/${Date.now()}_${slipFile.name}`);
      await uploadBytes(slipRef, slipFile);
      const slipUrl = await getDownloadURL(slipRef);

      const paymentRecord: Omit<PaymentRecord, 'id'> = {
        userId: user.uid,
        email: user.email || '',
        amount: checkoutData.amount,
        billingPeriod: checkoutData.billingPeriod,
        plan: 'start',
        addons: {
          hasPathUrl: checkoutData.hasPathUrl,
          hasSubdomain: checkoutData.hasSubdomain,
          username: checkoutData.username || null,
        },
        status: isEmailVerified ? 'approved' : 'pending',
        paymentMethod: 'promptpay',
        promptPayRef: paymentRef,
        slipImageUrl: slipUrl,
        createdAt: Timestamp.now(),
        verifiedAt: isEmailVerified ? Timestamp.now() : null,
        expiresAt: Timestamp.fromDate(expiryTime!),
      };

      await addDoc(collection(db, 'salePagePayments'), paymentRecord);

      if (isEmailVerified) {
        // Auto-activate subscription for verified users
        const { updateDoc } = await import('firebase/firestore');
        const endDate = checkoutData.billingPeriod === 'yearly' 
          ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
          : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

        await updateDoc(doc(db, 'users', user.uid), {
          'salePageSubscription.status': 'active',
          'salePageSubscription.plan': 'start',
          'salePageSubscription.billingPeriod': checkoutData.billingPeriod,
          'salePageSubscription.startDate': new Date(),
          'salePageSubscription.endDate': endDate,
          'salePageSubscription.addons': {
            hasPathUrl: checkoutData.hasPathUrl,
            hasSubdomain: checkoutData.hasSubdomain,
            username: checkoutData.username || null,
          }
        });

        // Send welcome email
        try {
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const { app } = await import('../../lib/firebase');
          const functions = getFunctions(app, 'us-central1');
          const sendWelcomeEmail = httpsCallable(functions, 'sendPaymentWelcomeEmail');
          await sendWelcomeEmail({ 
            email: user.email,
            name: userData?.displayName || user.email,
            billingPeriod: checkoutData.billingPeriod
          });
        } catch (emailError) {
          console.error('Error sending welcome email:', emailError);
        }

        setPaymentStatus('uploaded');
        alert('🎉 ชำระเงินสำเร็จ! บัญชีของคุณเปิดใช้งานแล้ว\n\nคุณจะได้รับอีเมลยืนยันการเริ่มใช้งาน');
        
        setTimeout(() => {
          navigate('/salepage/dashboard');
        }, 2000);
      } else {
        setPaymentStatus('uploaded');
        alert('✅ ส่งสลิปสำเร็จ! กรุณา Login เพื่อดูสถานะการชำระเงิน\n\nทีมงานจะตรวจสอบและแจ้งผลทางอีเมล');
        
        // Redirect to login page
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              from: '/salepage/dashboard',
              paymentPending: true 
            } 
          });
        }, 2000);
      }

    } catch (error) {
      console.error('Error submitting payment:', error);
      alert('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      setPaymentStatus('pending');
    } finally {
      setIsUploading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const generatePromptPayQR = () => {
    const amount = checkoutData?.amount || 0;
    return `https://promptpay.io/${PROMPTPAY_NUMBER}/${amount}.png`;
  };

  if (!checkoutData) {
    return null;
  }

  return (
    <>
      <SalePageNavbar />
      <div className="min-h-screen bg-[#F3F8F8] py-12">
        <div className="container mx-auto px-4 max-w-4xl">
        <button
          onClick={() => navigate('/salepage/checkout')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">กลับ</span>
        </button>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">ชำระเงิน</h1>
          <p className="text-gray-600">สแกน QR Code หรือโอนเงินผ่าน PromptPay</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">ข้อมูลการชำระเงิน</h2>
              {expiryTime && (
                <div className="flex items-center gap-2 text-blue-600">
                  <Clock className="w-5 h-5" />
                  <span className="font-semibold">{timeRemaining}</span>
                </div>
              )}
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-8 text-white mb-6">
              <div className="text-center mb-6">
                <img
                  src={generatePromptPayQR()}
                  alt="PromptPay QR Code"
                  className="w-64 h-64 mx-auto bg-white p-4 rounded-xl"
                />
              </div>
              
              <div className="text-center">
                <div className="text-sm opacity-90 mb-1">จำนวนเงิน</div>
                <div className="text-4xl font-bold mb-4">{checkoutData.amount.toLocaleString()}฿</div>
                <div className="text-sm opacity-90">
                  {checkoutData.billingPeriod === 'yearly' ? 'ชำระรายปี' : 'ชำระรายเดือน'}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">เลขพร้อมเพย์</span>
                  <button
                    onClick={() => copyToClipboard(PROMPTPAY_NUMBER)}
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4" />
                        คัดลอกแล้ว
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        คัดลอก
                      </>
                    )}
                  </button>
                </div>
                <div className="text-lg font-bold text-gray-900">{PROMPTPAY_NUMBER}</div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-sm text-gray-600 mb-1">ชื่อบัญชี</div>
                <div className="text-lg font-bold text-gray-900">{PROMPTPAY_NAME}</div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">เลขอ้างอิง</span>
                  <button
                    onClick={() => copyToClipboard(paymentRef)}
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                  >
                    <Copy className="w-4 h-4" />
                    คัดลอก
                  </button>
                </div>
                <div className="text-lg font-bold text-gray-900 font-mono">{paymentRef}</div>
              </div>
            </div>

            <div className="mt-6 bg-blue-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <div className="font-semibold mb-1">วิธีการชำระเงิน:</div>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>สแกน QR Code หรือโอนเงินผ่าน PromptPay</li>
                    <li>ใส่เลขอ้างอิงในช่องหมายเหตุ</li>
                    <li>อัปโหลดสลิปการโอนเงินด้านขวา</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">อัปโหลดสลิปการโอนเงิน</h2>

            {paymentStatus === 'pending' && (
              <div className="space-y-6">
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-500 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSlipUpload}
                    className="hidden"
                    id="slip-upload"
                  />
                  <label htmlFor="slip-upload" className="cursor-pointer">
                    {slipPreview ? (
                      <div>
                        <img
                          src={slipPreview}
                          alt="Payment Slip Preview"
                          className="max-w-full max-h-64 mx-auto rounded-lg mb-4"
                        />
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setSlipFile(null);
                            setSlipPreview(null);
                          }}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          ลบรูป
                        </button>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-700 font-medium mb-2">คลิกเพื่ออัปโหลดสลิป</p>
                        <p className="text-sm text-gray-500">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</p>
                      </div>
                    )}
                  </label>
                </div>

                <button
                  onClick={handleSubmitPayment}
                  disabled={!slipFile || isUploading}
                  className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-xl font-semibold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      กำลังอัปโหลด...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      ยืนยันการชำระเงิน
                    </>
                  )}
                </button>
              </div>
            )}

            {paymentStatus === 'uploaded' && (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">ส่งสลิปสำเร็จ!</h3>
                <p className="text-gray-600 mb-4">
                  เรากำลังตรวจสอบการชำระเงินของคุณ
                </p>
                <p className="text-sm text-gray-500">
                  คุณจะได้รับอีเมลยืนยันภายใน 5-10 นาที
                </p>
              </div>
            )}

            <div className="mt-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3">สรุปแพ็กเกจ</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">แพ็กเกจ</span>
                    <span className="font-semibold text-gray-900">Start Plan</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">ระยะเวลา</span>
                    <span className="font-semibold text-gray-900">
                      {checkoutData.billingPeriod === 'yearly' ? '1 ปี' : '1 เดือน'}
                    </span>
                  </div>
                  {checkoutData.hasPathUrl && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Path URL</span>
                      <span className="font-semibold text-gray-900">edumi.co/@{checkoutData.username}</span>
                    </div>
                  )}
                  {checkoutData.hasSubdomain && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subdomain</span>
                      <span className="font-semibold text-gray-900">{checkoutData.username}.edumi.co</span>
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="font-bold text-gray-900">ยอดรวม</span>
                      <span className="font-bold text-gray-900 text-lg">{checkoutData.amount.toLocaleString()}฿</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <div className="font-semibold mb-1">หมายเหตุ:</div>
                    <ul className="list-disc list-inside space-y-1">
                      <li>การชำระเงินจะถูกตรวจสอบภายใน 5-10 นาที</li>
                      <li>กรุณาอัปโหลดสลิปที่ชัดเจน</li>
                      <li>หากมีปัญหา ติดต่อ support@edumi.co</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default SalePagePayment;
